import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../utils/api-fetch';
import type { EmailSiteSummary, EmailSitesResponse } from '../types/email-sites';

const STORAGE_KEY = 'bulk-email-selected-site';

export function useEmailSites() {
  const [sites, setSites] = useState<EmailSiteSummary[]>([]);
  const [allBrands, setAllBrands] = useState<EmailSiteSummary[]>([]);
  const [defaultSiteId, setDefaultSiteId] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSites = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/bulk-email/sites');
      const data = (await response.json()) as EmailSitesResponse;
      if (!response.ok || !data.success) {
        throw new Error('Could not load website list');
      }
      setSites(data.sites);
      setAllBrands(
        Array.isArray(data.allBrands) && data.allBrands.length > 0 ? data.allBrands : data.sites
      );
      setDefaultSiteId(data.defaultSiteId);

      const stored = localStorage.getItem(STORAGE_KEY);
      const initial =
        stored && data.sites.some((s) => s.id === stored) ? stored : data.defaultSiteId;
      setSelectedSiteId(initial);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load websites');
      setSites([]);
      setAllBrands([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchSites();
  }, [fetchSites]);

  const selectSite = useCallback((siteId: string) => {
    setSelectedSiteId(siteId);
    localStorage.setItem(STORAGE_KEY, siteId);
  }, []);

  const selectedSite = sites.find((s) => s.id === selectedSiteId) ?? null;

  return {
    sites,
    allBrands,
    defaultSiteId,
    selectedSiteId,
    selectedSite,
    loading,
    error,
    selectSite,
    refreshSites: fetchSites,
  };
}
